import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./TextEditor.module.css";
import ReactSummernote from "react-summernote";
import $ from "jquery";

import SelectColorInput from "../SelectColorInput";
import ButtonStyledRadioButtonGroup from "../ButtonStyledRadioButtonGroup";
import Button from "../Button";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faFont,
  faTextWidth,
} from "@fortawesome/free-solid-svg-icons";

import Markdown from "shared/Markdown";

window.jQuery = $;
require("bootstrap");

/**
 * Interactive component.
 * A WYSIWYG text editor from Summernote.
 */
export function TextEditor({ initialContent, settings, onClick }) {
  const defaultSettings = {
    textColor: "default #000",
    customColor: "#fff",
    alignment: "left",
    size: "Normal",
    width: "Normal",
  };

  const normalizeSettings = (source) => ({
    textColor: source?.textColor ?? defaultSettings.textColor,
    customColor: source?.customColor ?? defaultSettings.customColor,
    alignment: source?.alignment ?? source?.align ?? defaultSettings.alignment,
    size: source?.size ?? source?.fontSize ?? defaultSettings.size,
    width: source?.width ?? defaultSettings.width,
  });

  const areSettingsEqual = (a, b) =>
    a?.textColor === b?.textColor &&
    a?.customColor === b?.customColor &&
    a?.alignment === b?.alignment &&
    a?.size === b?.size &&
    a?.width === b?.width;

  const [textContent, setTextContent] = useState(initialContent);
  useEffect(() => {
    setTextContent(initialContent);
  }, [initialContent]);

  // Ref to access Summernote instance for codeview toggling events
  const editorRef = useRef();

  const [newSettings, setNewSettings] = useState(() =>
    normalizeSettings(settings)
  );

  useEffect(() => {
    const normalized = normalizeSettings(settings);
    setNewSettings((current) =>
      areSettingsEqual(current, normalized) ? current : normalized
    );
  }, [settings]);

  // Guard against rendering loops
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    onClick(textContent, newSettings);
  }, [textContent, newSettings]);

  const getRGBFromColor = (color) => {
    const match = color.match(
      /(rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*\d*(?:\.\d+)?)?\s*\))/
    );
    return match ? match[1] : "";
  };

  const [textColor, setTextColor] = useState(() => {
    const color = getRGBFromColor(newSettings.textColor);
    return color || "#000"; // Return extracted color or default to "#000"
  });
  useEffect(() => {
    const color = getRGBFromColor(newSettings.textColor);
    setTextColor(color || "#000"); // Update textColor state, default to "#000" if no color found
  }, [newSettings.textColor]);

  const [isColorOpen, setIsColorOpen] = useState(false);

  const colorRef = useRef();
  useOutsideAlerter(colorRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setIsColorOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const handleSettingsChange = (key, value) =>
    setNewSettings({ ...newSettings, [key]: value });

  const [hidden, setHidden] = useState(false);

  // const StringToHtml = (text) =>{
  //   const [html, setHtml] = useState<string>("")
  //   useEffect(() => {
  //     setHtml(text)
  //   }, [html])
  //   return(
  //       <div>{html}</div>
  //   )
  // }

  const handleDoneClick = () => {
    onClick(textContent, newSettings);
    setHidden(false);
  };

  return (
    <>
      {!hidden ? (
        <div className={styles.border}>
          <div onClick={() => setHidden(true)}>
            <div className={styles.newContainer}>
              <h3 className={`${styles.text} ${styles.renderedContent}`}>
                {" "}
                {textContent ? (
                  <Markdown
                    content={textContent}
                    style={{ color: textColor }}
                  />
                ) : (
                  "NEW TEXT ELEMENT"
                )}{" "}
              </h3>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.container}>
          <link
            href="https://stackpath.bootstrapcdn.com/bootstrap/3.4.1/css/bootstrap.min.css"
            rel="stylesheet"
          />
          <script src="https://code.jquery.com/jquery-3.5.1.min.js"></script>
          <script src="https://stackpath.bootstrapcdn.com/bootstrap/3.4.1/js/bootstrap.min.js"></script>

          <link
            href="https://cdn.jsdelivr.net/npm/summernote@0.8.18/dist/summernote.min.css"
            rel="stylesheet"
          />
          <script src="https://cdn.jsdelivr.net/npm/summernote@0.8.18/dist/summernote.min.js"></script>
          <div className={styles.header}>
            <div ref={colorRef}>
              <SelectColorInput
                currentColor={newSettings.textColor}
                setCurrentColor={(e) =>
                  handleSettingsChange(
                    "textColor",
                    `${e.target.title} ${e.target.style.backgroundColor}`
                  )
                }
                customColor={newSettings.customColor}
                setCustomColor={(value) =>
                  handleSettingsChange("customColor", value)
                }
                isOpen={isColorOpen}
                setIsOpen={() => setIsColorOpen((isColorOpen) => !isColorOpen)}
              />
            </div>
            <ButtonStyledRadioButtonGroup
              currentlySelected={newSettings.alignment}
              setCurrentlySelected={(e) => {
                const value = e.currentTarget?.value || e.target?.value;
                if (value && newSettings.alignment !== value)
                  handleSettingsChange("alignment", value);
              }}
              options={[
                {
                  value: "left",
                  buttonContent: <FontAwesomeIcon icon={faAlignLeft} />,
                },
                {
                  value: "center",
                  buttonContent: <FontAwesomeIcon icon={faAlignCenter} />,
                },
                {
                  value: "right",
                  buttonContent: <FontAwesomeIcon icon={faAlignRight} />,
                },
              ]}
            />
            {[]}
            <div className={styles["increment-group"]}>
              <ButtonStyledRadioButtonGroup
                currentlySelected={newSettings.size}
                setCurrentlySelected={(e) => {
                  const value = e.currentTarget?.value || e.target?.value;
                  if (value && newSettings.size !== value)
                    handleSettingsChange("size", value);
                }}
                options={["Small", "Normal", "Large", "Header2", "Header1"].map(
                  (value) => {
                    return {
                      value: value,
                      buttonContent: <FontAwesomeIcon icon={faFont} />,
                    };
                  }
                )}
              />
            </div>
            <div className={styles["increment-group"]}>
              <ButtonStyledRadioButtonGroup
                currentlySelected={newSettings.width}
                setCurrentlySelected={(e) => {
                  const value = e.currentTarget?.value || e.target?.value;
                  if (value && newSettings.width !== value)
                    handleSettingsChange("width", value);
                }}
                options={[
                  "Wide-xs",
                  "Wide-sm",
                  "Normal",
                  "Wide-md",
                  "Wide-lg",
                ].map((value) => {
                  return {
                    value: value,
                    buttonContent: <FontAwesomeIcon icon={faTextWidth} />,
                  };
                })}
              />
            </div>
            <Button
              text="Done"
              onClick={handleDoneClick}
              className={styles.button}
            />
          </div>
          <ReactSummernote
            ref={editorRef}
            className={styles.summernote}
            onInit={({ summernote }) => {
              if (initialContent) summernote("code", initialContent);

              // Attach listener to capture content when toggling codeview
              if (editorRef.current) {
                $(editorRef.current).on("summernote.codeview.toggled", () => {
                  const updated = $(editorRef.current).summernote("code");
                  setTextContent(updated);
                });
              }

              $(".note-toolbar [data-original-title]").each(function () {
                const $el = $(this);
                const label =
                  $el.attr("data-original-title") || $el.attr("title") || "";
                const $btn = $el.closest("button, a");
                if ($btn.length) {
                  $btn.attr("data-label", label);
                } else {
                  $el.attr("data-label", label);
                }
                $el.removeAttr("data-original-title").removeAttr("title");
              });
            }}
            options={{
              disableDragAndDrop: true,
              height: 200,
              placeholder: "Enter Section Text",
              toolbar: [
                [
                  "styleWithFont",
                  ["style", "bold", "italic", "underline", "clear"],
                ],
                ["color", ["color"]],
                ["fontFormat", ["superscript", "subscript"]],
                ["list", ["ul", "ol"]],
                ["fontsize"],
                ["table", ["table"]],
                ["insert", ["link", "picture", "video"]],
                ["view", ["codeview"]], // Enable HTML code view toggle
              ],
              codeviewFilter: true,
              codeviewIframeFilter: true,
              dialogsInBody: true,
              callbacks: {
                onChange: (contents) => setTextContent(contents),
              },
            }}
            onChange={(e) => setTextContent(e)}
            onImageUpload={(files) => {
              [...files].forEach((file) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  ReactSummernote.insertImage(reader.result, ($image) => {
                    $image.css({
                      width: "100%",
                      height: "auto",
                    });
                    $(".note-popover [data-original-title]").each(function () {
                      const $el = $(this);
                      const label =
                        $el.attr("data-original-title") ||
                        $el.attr("title") ||
                        "";
                      const $btn = $el.closest("button, a");
                      if ($btn.length) {
                        $btn.attr("data-label", label);
                      } else {
                        $el.attr("data-label", label);
                      }
                      $el.removeAttr("data-original-title").removeAttr("title");
                    });
                  });
                };
                reader.readAsDataURL(file);
              });
            }}
          />
        </div>
      )}
    </>
  );
}

TextEditor.propTypes = {
  /**
   * String to import on initialization.
   * This can be an HTML string or plain string.
   */
  initialContent: PropTypes.string,
  /**
   * Optional object with previous settings if importing.
   */
  settings: PropTypes.shape({
    /**
     * String with color title and color.
     * Ex. 'default #fff'
     */
    textColor: PropTypes.string,
    customColor: PropTypes.string,
    alignment: PropTypes.oneOf(["left", "center", "right"]),
    size: PropTypes.oneOf(["Small", "Normal", "Large", "Header2", "Header1"]),
    width: PropTypes.oneOf([
      "Wide-xs",
      "Wide-sm",
      "Wide-md",
      "Wide-lg",
      "Normal",
    ]),
  }),
  /**
   * Function that fires when the 'Done' button is clicked.
   * Receives the text from the Text Editor as the argument as well as the new settings.
   */
  onClick: PropTypes.func,
};
